import React, { useId, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  LntButton,
  LntDatePicker,
  LntDrawer,
  LntForm,
  LntFormGroup,
  LntFormItem,
  LntInput,
  LntSelect,
  LntTabs
} from '@app/components/base'
import {
  TranslationsAddClustersDrawer,
  TranslationsAddMonitoringTypesDrawer,
  TranslationsAddCompetitorsDrawer
} from '@app/drawers'

import { useNotification, useToggle } from '@app/hooks'

import { SIZE, dateFormat } from '@app/constants'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { DAY_OF_WEEK_OPTIONS } from '@app/constants/week'
import { TRANSLATIONS_CONFIG_KEY } from '@app/constants/queryKeys'

import { getTranslationsConfigDataFromResponse } from '@app/utils/responseAdapters'
import { isEmptyObject } from '@app/utils/object'
import { getFormattedDate } from '@app/utils/date'

import { getTranslationsConfig, patchTranslationsConfig } from '@app/api/routes/translationsConfig'

import { TABS_KEYS } from './constants'

import { MonitoringTypeTab, CompetitorsTab, ClusterTab } from './Tabs'

import styles from './styles.module.scss'

import type { PriceTranslationDrawerProps } from './types'
import type { TabsProps } from 'antd'
import type { PatchTranslationsCompetitorsData } from '@app/api/routes/translationsConfig/types'

const title = 'Транслирование цен'

export const PriceTranslationDrawer: React.FC<PriceTranslationDrawerProps> = props => {
  const { onClose } = props

  const listCompetitorsDrawerToggle = useToggle()
  const addMonitoringTypesDrawerToggle = useToggle()
  const addClustersDrawerToggle = useToggle()

  const formId = useId()

  const queryClient = useQueryClient()
  const notification = useNotification()
  const form = useForm<PatchTranslationsCompetitorsData>()

  const { data: configData } = useQuery({
    queryKey: [TRANSLATIONS_CONFIG_KEY.config],
    queryFn: async () => await getTranslationsConfig()
  })

  const patchConfigDataMutation = useMutation({
    mutationFn: patchTranslationsConfig,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [TRANSLATIONS_CONFIG_KEY.config] })

      notification.success({
        message: 'Транслирование цен успешно обновлено'
      })

      onClose()
    }
  })

  const configDataSource = useMemo(() => getTranslationsConfigDataFromResponse(configData), [configData])

  const handleSubmit = (data: PatchTranslationsCompetitorsData): void => {
    const { startAtTime, ...restData } = data
    const formattedTime = getFormattedDate(startAtTime, dateFormat.HH_mm_ss)

    if (formattedTime) {
      patchConfigDataMutation.mutate({ ...restData, startAtTime: formattedTime })
    }
  }

  const footerButtons: React.ReactNode = (
    <div className={ styles.priceTranslationDrawer__drawerFooter }>
      <LntButton
        htmlType='submit'
        form={ formId }
        disabled={ isEmptyObject(form.formState.dirtyFields) }
      >
        Сохранить
      </LntButton>
    </div>
  )

  const tabs: TabsProps['items'] = [
    {
      label: 'Магазин',
      key: TABS_KEYS.competitorTab,
      children: <CompetitorsTab onOpenAddListCompetitorsDrawer={ listCompetitorsDrawerToggle.setToggled } />
    },
    {
      label: 'Тип мониторинга',
      key: TABS_KEYS.monitoringTypeTab,
      children: <MonitoringTypeTab onOpenAddMonitoringTypesDrawer={ addMonitoringTypesDrawerToggle.setToggled } />
    },
    {
      label: 'Кластер',
      key: TABS_KEYS.clusterTab,
      children: <ClusterTab onOpenAddClustersDrawer={ addClustersDrawerToggle.setToggled } />
    }
  ]

  return (
    <LntDrawer
      { ...props }
      title={ title }
      width={ 1080 }
      footer={ footerButtons }
    >
      <LntForm
        layout='horizontal'
        className={ styles.priceTranslationDrawer__form }
        formInstance={ form }
        onSubmit={ handleSubmit }
        id={ formId }
      >
        <div className={ styles.priceTranslationDrawer__formTop }>
          <LntFormItem
            name='historicalDepthPerDays'
            defaultValue={ configDataSource?.historicalDepthPerDays }
            label='Период обращения в историю цен'
          >
            <LntInput
              className={ styles.priceTranslationDrawerFormTop__inputDays }
              placeholder='Кол-во дней'
              type='number'
            />
          </LntFormItem>
          <LntFormGroup label='Частота транслирования цен'>
            <div className={ styles.priceTranslationDrawer__formGroup }>
              <LntFormItem
                className={ styles.priceTranslationDrawerFormTop__inputWeek }
                name='startAtDayOfWeek'
                defaultValue={
                  DAY_OF_WEEK_OPTIONS.find(option => option.value === configDataSource?.startAtDayOfWeek)?.value ?? 1
                }
              >
                <LntSelect
                  placeholder={ FILTERS_PLACEHOLDERS.daysOfWeek }
                  options={ DAY_OF_WEEK_OPTIONS }
                  showArrow
                  size={ SIZE.middle }
                />
              </LntFormItem>
              <LntFormItem
                name='startAtTime'
                defaultValue={ dayjs(configData?.startAtTime, dateFormat.HH_mm_ss) ?? null }
              >
                <LntDatePicker
                  picker='time'
                  size={ SIZE.middle }
                  placeholder={ FILTERS_PLACEHOLDERS.setTime }
                  format={ dateFormat.HH_mm_ss }
                />
              </LntFormItem>
            </div>
          </LntFormGroup>
        </div>

        <LntTabs items={ tabs } />
      </LntForm>

      <TranslationsAddCompetitorsDrawer
        open={ listCompetitorsDrawerToggle.value }
        onClose={ listCompetitorsDrawerToggle.setUntoggled }
      />

      <TranslationsAddMonitoringTypesDrawer
        open={ addMonitoringTypesDrawerToggle.value }
        onClose={ addMonitoringTypesDrawerToggle.setUntoggled }
      />

      <TranslationsAddClustersDrawer
        open={ addClustersDrawerToggle.value }
        onClose={ addClustersDrawerToggle.setUntoggled }
      />
    </LntDrawer>
  )
}
